import { Box } from "@mui/material";
import { cs, Icons } from "@repo/ui";
import { type FallbackUI, DisplayableError } from ".";
import Attention from "../AttentionBlock";
import { ModalContent } from "../Drawer/ModalContent";
import { ModalFooter } from "../Drawer/ModalFooter";
import ModalHeader from "../Drawer/ModalHeader";
import ErrorContent from "../ErrorPage/ErrorContent";
import { type IErrorMessage, GeneralMessage } from "../ErrorPage/GeneralMessage";
import { Trigger, TriggerButton } from "../Link";

export const GetDefaultErrorUI: (uiProps?: IErrorMessage) => FallbackUI = (uiProps) => {
    const DefaultErrorUI: FallbackUI = ({ error }) => {
        const displayElement = (() => {
            if (error instanceof DisplayableError) {
                if ("customJSX" in error.display) {
                    return error.display.customJSX;
                }
                return (
                    <GeneralMessage
                        title={error.display.title ?? uiProps?.title}
                        message={error.display.message ?? uiProps?.message}
                        buttonText={error.display.buttonText ?? uiProps?.buttonText}
                        errorMessage={error.message}
                        customButton={
                            error.display.customButton !== undefined
                                ? error.display.customButton
                                : uiProps?.customButton
                        }
                    />
                );
            }
            return <GeneralMessage {...uiProps} errorMessage={error.message} />;
        })();

        return <ErrorContent>{displayElement}</ErrorContent>;
    };
    return DefaultErrorUI;
};

export const DefaultErrorUI: FallbackUI = GetDefaultErrorUI();

export const DefaultErrorUIWithCard: FallbackUI = ({ error }) => {
    const ErrorUI = GetDefaultErrorUI();
    return <ErrorUI error={error} />;
};

export const DefaultErrorUIWithTheme: FallbackUI = ({ error }) => {
    return <DefaultErrorUI error={error} />;
};

type IDefaultModalUIErrorWithProps = {
    onClose: () => void;
} & IErrorMessage;

export const GetDefaultModalErrorUI: (modalProps: IDefaultModalUIErrorWithProps) => FallbackUI = ({
    onClose,
    buttonText = cs.global.close,
    message = cs.errors.general,
    title = cs.errors.somethingWentWrong,
}) => {
    const DefaultModalError: FallbackUI = ({ error }) => {
        const isDisplayError = error instanceof DisplayableError;
        const modalContent = (() => {
            if (isDisplayError && "customJSX" in error.display) return error.display.customJSX;
            return (
                <ErrorContent sx={{ height: "100%", minHeight: "auto" }}>
                    <Attention
                        severity="error"
                        title={
                            isDisplayError && "title" in error.display && error.display.title !== undefined
                                ? error.display.title
                                : title
                        }
                    >
                        {isDisplayError && "message" in error.display && error.display.message !== undefined
                            ? error.display.message
                            : message}
                    </Attention>
                </ErrorContent>
            );
        })();
        const button = (() => {
            if (error instanceof DisplayableError) {
                if (!("customJSX" in error.display)) {
                    if (error.display.customButton !== undefined) {
                        return error.display.customButton;
                    }
                    return <TriggerButton onClick={onClose}>{error.display.buttonText ?? buttonText}</TriggerButton>;
                }
            }
            return buttonText;
        })();

        return (
            <>
                <ModalHeader>
                    <Box sx={{ visibility: "hidden" }} />
                    <Trigger onClick={onClose}>
                        <Icons.Close sx={{ width: "2rem", height: "2rem" }} />
                    </Trigger>
                </ModalHeader>
                <ModalContent>{modalContent}</ModalContent>
                <ModalFooter>{button}</ModalFooter>
            </>
        );
    };
    return DefaultModalError;
};
